import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChatCircle } from '@phosphor-icons/react';

import { getChatUnread } from '@/api/chat';
import { Button } from '@/components/ui/button';

// ChatHeaderButton is the chat entry point in the top bar — a ghost icon button
// that deep-links to the org's chat page and carries a lime unread badge.
// Chat used to live as a sidebar menu item; it was promoted to the header so
// it's one click away from every page and the unread count is always visible.
//
// The unread count comes from GET /chat/unread (a cheap single-number query,
// per-org counterpart to the mobile home badge). We poll on a low-frequency
// interval and refetch on window focus — same safety-net cadence the
// NotificationBell uses. The Chat page invalidates ['chat', 'unread', slug]
// when the user reads a thread or a new message arrives, so the badge also
// updates instantly while the user is actively chatting.

export interface ChatHeaderButtonProps {
  slug: string;
}

const POLL_MS = 60_000;

export function ChatHeaderButton({ slug }: ChatHeaderButtonProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const query = useQuery({
    queryKey: ['chat', 'unread', slug],
    queryFn: () => getChatUnread(slug, token),
    enabled: !!token && !!slug,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    // A member/guardian with no chat access (or a transient error) shouldn't
    // spam retries — the badge just stays hidden.
    retry: false,
    staleTime: 30_000,
  });

  const unread = query.data ?? 0;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={t('chat.openMenu', { count: unread })}
      asChild
      className="relative"
    >
      <Link to={`/orgs/${slug}/chat`}>
        <ChatCircle className="h-5 w-5" />
        {unread > 0 && (
          <span
            aria-hidden
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-pill bg-[var(--lime,#BEE034)] px-1 font-heading text-[0.6875rem] font-bold leading-none text-[var(--ink,#26272B)]"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Link>
    </Button>
  );
}
